import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LifeBuoy, ChevronDown, ChevronUp, MessageSquarePlus, Loader2 } from "lucide-react";
import api from "../services/api";
import { Button } from "./ui/button";

const STATUS_STYLES = {
  open:      "bg-orange-50 text-orange-700 border-orange-200",
  in_review: "bg-blue-50 text-blue-700 border-blue-200",
  resolved:  "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const STATUS_LABELS = {
  open: "Open",
  in_review: "In review",
  resolved: "Resolved",
};

function StatusPill({ status }) {
  const cls = STATUS_STYLES[status] || "bg-slate-50 text-slate-600 border-slate-200";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wide ${cls}`}
      data-testid={`support-status-${status}`}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

/**
 * Sprint-90 — Customer dashboard widget showing the latest 5 support
 * tickets. Collapses by default when there are zero tickets to keep the
 * dashboard uncluttered for new users.
 */
export default function SupportHistoryCard() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/customer/support/tickets", { params: { limit: 5 } });
        if (cancelled) return;
        setItems(data.items || []);
        setTotal(data.total || 0);
        // Collapse if zero — keep expanded otherwise.
        setExpanded((data.total || 0) > 0);
      } catch {
        if (!cancelled) {
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden animate-fadeInUp"
      data-testid="support-history-card"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition"
        data-testid="support-history-toggle"
      >
        <span className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center">
            <LifeBuoy className="h-5 w-5 text-[#FF6B00]" />
          </span>
          <span className="text-left">
            <span className="block font-['Outfit'] text-base font-bold text-slate-900">
              My Support Tickets
            </span>
            <span className="block text-xs text-slate-500">
              {loading ? "Loading…" : total === 0 ? "No tickets yet" : `${total} ticket${total === 1 ? "" : "s"} total`}
            </span>
          </span>
        </span>
        {expanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
      </button>

      {expanded && (
        <div className="border-t border-slate-100">
          {loading ? (
            <div className="p-6 flex justify-center" data-testid="support-history-loading">
              <Loader2 className="h-5 w-5 animate-spin text-slate-300" />
            </div>
          ) : items.length === 0 ? (
            <div className="p-6 text-center" data-testid="support-history-empty">
              <p className="text-sm text-slate-500">You haven't raised any support requests yet.</p>
              <Link to="/dashboard/contact-us">
                <Button
                  size="sm"
                  className="mt-3 bg-[#FF6B00] hover:bg-orange-600 text-white"
                  data-testid="support-history-cta-empty"
                >
                  <MessageSquarePlus className="h-4 w-4 mr-1.5" />
                  Contact support
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <ul className="divide-y divide-slate-50" data-testid="support-history-list">
                {items.map((t) => {
                  const unread = t.unread_admin_count || 0;
                  return (
                    <li key={t.ticket_id} className="hover:bg-slate-50/60 transition" data-testid={`support-history-item-${t.ticket_id}`}>
                      <Link
                        to={`/dashboard/support/tickets/${t.ticket_id}`}
                        className="px-5 py-3.5 flex items-start gap-3 w-full"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-xs font-semibold text-slate-900">
                              {t.ticket_id}
                            </span>
                            <span className="text-xs text-slate-500">·</span>
                            <span className="text-xs text-slate-600">{t.category_label}</span>
                            <StatusPill status={t.status} />
                            {unread > 0 && (
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-rose-500 text-white text-[10px] font-bold"
                                data-testid={`support-unread-badge-${t.ticket_id}`}
                              >
                                {unread} new
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-700 mt-1 line-clamp-2">
                            {t.message_preview}
                          </p>
                          <p className="text-[11px] text-slate-400 mt-1">{formatWhen(t.created_at)}</p>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
              <div className="px-5 py-3 bg-slate-50 flex items-center justify-between border-t border-slate-100">
                <Link
                  to="/dashboard/contact-us"
                  className="text-xs font-semibold text-[#FF6B00] hover:underline"
                  data-testid="support-history-new-link"
                >
                  + New support request
                </Link>
                {total > items.length && (
                  <span className="text-[11px] text-slate-400">Showing latest {items.length} of {total}</span>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
